import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { RiskNote } from "../components/layout/RiskDisclaimer";
import { Link } from "react-router-dom";
import {
  Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, ComposedChart,
} from "recharts";

const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function Simulator() {
  const { t, lang } = useI18n();
  const [amount, setAmount] = useState(5000);
  const [months, setMonths] = useState(36);
  const [scenario, setScenario] = useState("central");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api.post("/simulator", { amount, horizon_months: months, scenario }, { signal: controller.signal })
      .then((r) => setResult(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [amount, months, scenario]);

  const chartData = useMemo(() => result?.points || [], [result]);

  return (
    <div>
      <section className="section border-b border-slate-200">
        <div className="container-page">
          <div className="label-divider mb-6">{t.nav.simulator}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900" data-testid="simulator-title">
            {t.simulator.title}
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">{t.simulator.subtitle}</p>
        </div>
      </section>

      <section className="section">
        <div className="container-page grid lg:grid-cols-12 gap-10">
          {/* Controls */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-slate-200 rounded-sm p-8 sticky top-24">
              <div className="space-y-10">
                <div>
                  <div className="flex justify-between items-baseline mb-3">
                    <Label className="text-sm font-medium text-slate-700">{t.simulator.amount}</Label>
                    <div className="font-mono font-semibold text-slate-900 text-xl">{fmt(amount)}</div>
                  </div>
                  <Slider
                    min={1000} max={50000} step={500}
                    value={[amount]}
                    onValueChange={(v) => setAmount(v[0])}
                    data-testid="simulator-amount-slider"
                  />
                  <div className="flex justify-between text-[11px] uppercase tracking-[0.15em] text-slate-500 mt-2 font-mono">
                    <span>1 000€</span><span>50 000€</span>
                  </div>
                  <Input
                    type="number"
                    min={1000}
                    max={1000000}
                    value={amount}
                    onChange={(e) => setAmount(Math.max(1000, Math.min(1000000, Number(e.target.value) || 1000)))}
                    className="mt-4 rounded-sm font-mono"
                    data-testid="simulator-amount-input"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-baseline mb-3">
                    <Label className="text-sm font-medium text-slate-700">{t.simulator.horizon}</Label>
                    <div className="font-mono font-semibold text-slate-900 text-xl">{months} m</div>
                  </div>
                  <Slider
                    min={12} max={60} step={6}
                    value={[months]}
                    onValueChange={(v) => setMonths(v[0])}
                    data-testid="simulator-horizon-slider"
                  />
                  <div className="flex justify-between text-[11px] uppercase tracking-[0.15em] text-slate-500 mt-2 font-mono">
                    <span>12m</span><span>60m</span>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">{t.simulator.scenario}</Label>
                  <RadioGroup value={scenario} onValueChange={setScenario} className="grid grid-cols-3 gap-2" data-testid="simulator-scenario-group">
                    {t.invest.scenarios.map((s) => (
                      <label
                        key={s.key}
                        className={`cursor-pointer border rounded-sm p-3 text-center transition-all ${
                          scenario === s.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400"
                        }`}
                        data-testid={`scenario-radio-${s.key}`}
                      >
                        <RadioGroupItem value={s.key} className="sr-only" />
                        <div className="text-[10px] uppercase tracking-[0.15em] opacity-70">{s.name}</div>
                        <div className="font-mono font-semibold text-lg mt-1">{s.yield}</div>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
              <Metric label={t.simulator.annual} value={result ? `${result.annual_yield}%` : "—"} testid="metric-annual" />
              <Metric label={t.simulator.projected_return} value={result ? fmt(result.total_return) : "—"} testid="metric-total" />
              <Metric label={t.simulator.monthly} value={result ? fmt(result.monthly_distribution * 6) : "—"} testid="metric-monthly" />
            </div>

            <div className="mt-6 bg-white border border-slate-200 rounded-sm p-6">
              <div className="label-divider mb-4">{lang === "fr" ? "Projection" : "Projection"}</div>
              <div className="h-80 min-h-[20rem] w-full">
                {chartData.length === 0 ? (
                  <div className="h-full w-full flex items-center justify-center text-xs uppercase tracking-[0.18em] text-slate-400" data-testid="chart-empty">
                    {lang === "fr" ? "Calcul de la projection…" : "Computing projection…"}
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gcum" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#059669" stopOpacity={0.22} />
                          <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="month" tickLine={false} axisLine={false} stroke="#64748B" fontSize={11} tickFormatter={(v) => `${v}m`} />
                      <YAxis tickLine={false} axisLine={false} stroke="#64748B" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k€`} />
                      <Tooltip
                        contentStyle={{ borderRadius: 2, borderColor: "#E2E8F0", fontSize: 12 }}
                        formatter={(v) => fmt(v)}
                        labelFormatter={(l) => `${lang === "fr" ? "Mois" : "Month"} ${l}`}
                      />
                      <Area type="monotone" dataKey="cumulative_distributed" name={lang === "fr" ? "Distribué cumulé" : "Cumulative distributed"} stroke="#059669" strokeWidth={2} fill="url(#gcum)" />
                      <Line type="monotone" dataKey="portfolio_value" name={lang === "fr" ? "Valeur portefeuille" : "Portfolio value"} stroke="#0F172A" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <Button asChild size="lg" className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="simulator-cta">
                <Link to="/register">{t.simulator.cta}</Link>
              </Button>
              <div className="text-xs text-slate-500 max-w-md">
                {result?.disclaimer || t.warning.short}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className="mt-2 font-mono text-2xl font-semibold text-slate-900 counter-in" key={value}>{value}</div>
    </div>
  );
}
